<template>
  <div v-if="showQr" class="qr-code">
    <QrcodeVue :value="qrValue" :size="256" level="H" />
  </div>
  <div v-else class="face-border">
    <div v-if="!isReady" class="loading-overlay">
      <Spinner size="lg" />
    </div>

    <div
      class="relative w-full h-full bg-gray-100 rounded-lg p-5 overflow-hidden"
    >
      <div class="relative w-full h-full z-[1000]">
        <div class="absolute top-0 left-0 w-full h-full">
          <svg-icon name="rounded-border" />
          <svg-icon
            name="rounded-border"
            class="rotate-90 absolute top-0 right-0"
          />
          <svg-icon
            name="rounded-border"
            class="-rotate-90 absolute bottom-0 left-0"
          />
          <svg-icon
            name="rounded-border"
            class="-rotate-180 absolute bottom-0 right-0"
          />
        </div>
        <div
          class="absolute top-0 left-0 w-full h-full overflow-hidden border border-solid rounded-lg border-gray-300"
        >
          <div v-if="isAnimationReady" class="scanning-line"></div>
        </div>
      </div>

      <div id="video-container" class="relative">
        <video
          ref="video"
          autoplay
          muted
          playsinline
          webkit-playsinline
          x5-playsinline
          x5-video-player-type="h5"
          x5-video-player-fullscreen="false"
          preload="metadata"
        ></video>
        <div
          ref="roi"
          class="absolute border-2 border-white rounded-lg"
          style="left: 10%; top: 10%; width: 80%; height: 80%"
        ></div>
      </div>

      <div v-if="isAnimationReady" class="instruction-text">
        <div>{{ instructionText }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import * as faceapi from "face-api.js";
import QrcodeVue from "qrcode.vue";
import {
  STRAIGHT_THRESHOLD_LOWER,
  STRAIGHT_THRESHOLD_UPPER,
  FACE_DETECT_TIMEOUT_MS,
} from "./constants";
import { Spinner } from "@/shared/ui/spinner";
import { showAlert } from "@/shared/store/alertStore";

export default {
  name: "AppFaceId",
  components: { QrcodeVue, Spinner },

  props: {
    externalError: { type: String, default: "" },
  },

  emits: [
    "change:step",
    "done",
    "error",
    "close",
    "sendFalseResult",
    "timeout",
  ],

  data() {
    return {
      isReady: false,
      samples: [],
      requiredSamples: 5,
      isAnimationReady: false,
      detections: [],
      stream: null,
      showQr: false,
      capturing: false,
      rafId: null,
      watchdogTimer: null,
      qrValue: location.href,
      finished: false,
    };
  },

  computed: {
    instructionText() {
      if (this.externalError) {
        return this.$t("❌ Ошибка - ") + this.externalError;
      }
      if (this.capturing) {
        return this.$t("Обрабатываем...");
      }
      return this.$t("Поместите лицо в центр камеры");
    },
  },

  async mounted() {
    try {
      await faceapi.tf.setBackend("cpu");
      await faceapi.tf.ready();
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri("/models"),
        faceapi.nets.faceLandmark68Net.loadFromUri("/models"),
      ]);
      this.isReady = true;
      await this.startVideo();
    } catch (err) {
      console.error("face-api models load failed:", err);
      this.$emit("error");
    }
  },

  beforeUnmount() {
    this.clearWatchdog();
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }
  },

  methods: {
    async startVideo() {
      const video = this.$refs.video;
      if (!video) return;

      try {
        const constraints = {
          video: {
            width: { min: 320, max: 1920 },
            height: { min: 240, max: 1080 },
            facingMode: "user",
            frameRate: { ideal: 30, min: 15 },
          },
          audio: false,
        };

        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        video.srcObject = this.stream;

        await new Promise((resolve, reject) => {
          video.onloadedmetadata = resolve;
          video.onerror = reject;
          setTimeout(resolve, 3000);
        });

        try {
          await video.play();
        } catch (playError) {
          console.warn("Auto-play failed:", playError);
          video.addEventListener("canplay", () => {
            video.play().catch(console.error);
          });
        }

        const startVideoProcessing = () => {
          setTimeout(() => {
            this.isAnimationReady = true;
          }, 1000);
          let lastDetectionTime = 0;
          const detectionInterval = 100;

          const processFrame = async (now) => {
            if (this.finished) {
              this.rafId = null;
              return;
            }
            if (this.capturing) {
              this.rafId = requestAnimationFrame(processFrame);
              return;
            }
            if (
              !lastDetectionTime ||
              now - lastDetectionTime > detectionInterval
            ) {
              lastDetectionTime = now;
              try {
                const detection = await faceapi
                  .detectSingleFace(
                    video,
                    new faceapi.TinyFaceDetectorOptions({ inputSize: 320 })
                  )
                  .withFaceLandmarks();
                if (detection) this.handleDetection(detection, video);
              } catch (err) {
                console.error("face-api detection failed:", err);
              }
            }
            if (this.finished) {
              this.rafId = null;
              return;
            }
            this.rafId = requestAnimationFrame(processFrame);
          };

          this._processFrame = processFrame;
          this.rafId = requestAnimationFrame(processFrame);
          this.startWatchdog();
        };

        const checkVideoReady = () => {
          if (video.readyState >= 2 && !video.paused) {
            startVideoProcessing();
            return true;
          }
          return false;
        };

        if (!checkVideoReady()) {
          video.addEventListener("play", startVideoProcessing, { once: true });
          video.addEventListener(
            "canplay",
            () => {
              if (!this.isAnimationReady) startVideoProcessing();
            },
            { once: true }
          );
          const checkInterval = setInterval(() => {
            if (checkVideoReady()) clearInterval(checkInterval);
          }, 100);
          setTimeout(() => clearInterval(checkInterval), 5000);
        }
      } catch (error) {
        console.error("Error starting video:", error.message);
        this.showQr = true;
        this.qrValue = location.href;
        showAlert(
          this.$t("Ошибка"),
          this.$t(
            "Не удалось запустить камеру. Пожалуйста, убедитесь, что камера доступна и разрешена для использования."
          ),
          "error"
        );
        this.$emit("error");
      }
    },

    stopProcessing() {
      this.finished = true;
      this.clearWatchdog();
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
    },

    pauseDetection() {
      this.finished = true;
      this.clearWatchdog();
    },

    startWatchdog() {
      this.clearWatchdog();
      this.watchdogTimer = setTimeout(() => {
        if (this.finished || this.capturing) return;
        this.pauseDetection();
        this.$emit("timeout");
      }, FACE_DETECT_TIMEOUT_MS);
    },

    clearWatchdog() {
      if (this.watchdogTimer) {
        clearTimeout(this.watchdogTimer);
        this.watchdogTimer = null;
      }
    },

    resume() {
      this.samples = [];
      this.detections = [];
      this.capturing = false;
      this.finished = false;
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      if (this._processFrame) {
        this.rafId = requestAnimationFrame(this._processFrame);
        this.startWatchdog();
      }
    },

    handleDetection(detection, video) {
      if (this.finished || this.capturing) return;
      const landmarks = detection.landmarks;
      const p0 = landmarks.positions[0];
      const p16 = landmarks.positions[16];
      const p30 = landmarks.positions[30];
      if (!p0 || !p16 || !p30) return;

      const ratio = (p30.x - p0.x) / (p16.x - p30.x);

      if (
        ratio > STRAIGHT_THRESHOLD_LOWER &&
        ratio < STRAIGHT_THRESHOLD_UPPER
      ) {
        this.samples.push(detection);
      } else {
        this.samples = [];
      }

      if (this.samples.length >= this.requiredSamples) {
        this.capturing = true;
        this.finish(video);
      }
    },

    async finish(video) {
      try {
        const image = await this.extractFaceImage(video);
        this.pauseDetection();
        if (image) {
          this.detections.push(image);
          this.$emit("done", this.detections);
        } else {
          this.$emit("sendFalseResult", this.detections);
        }
      } finally {
        this.capturing = false;
      }
    },

    async extractFaceImage(video) {
      try {
        const roiEl = this.$refs.roi;
        if (!roiEl) throw new Error("ROI element not found");

        const videoRect = video.getBoundingClientRect();
        const roiRect = roiEl.getBoundingClientRect();

        const xCss = roiRect.left - videoRect.left;
        const yCss = roiRect.top - videoRect.top;
        const wCss = roiRect.width;
        const hCss = roiRect.height;

        const vw = video.videoWidth;
        const vh = video.videoHeight;

        const fit = getComputedStyle(video).objectFit || "contain";

        let sx, sy, sw, sh;

        if (fit === "cover") {
          const scale = Math.max(videoRect.width / vw, videoRect.height / vh);
          const renderedW = vw * scale;
          const renderedH = vh * scale;
          const offsetX = (renderedW - videoRect.width) / 2;
          const offsetY = (renderedH - videoRect.height) / 2;
          sx = (xCss + offsetX) / scale;
          sy = (yCss + offsetY) / scale;
          sw = wCss / scale;
          sh = hCss / scale;
        } else {
          const scale = Math.min(videoRect.width / vw, videoRect.height / vh);
          const renderedW = vw * scale;
          const renderedH = vh * scale;
          const padX = (videoRect.width - renderedW) / 2;
          const padY = (videoRect.height - renderedH) / 2;
          sx = (xCss - padX) / scale;
          sy = (yCss - padY) / scale;
          sw = wCss / scale;
          sh = hCss / scale;
        }

        sx = Math.max(0, Math.min(vw - 1, sx));
        sy = Math.max(0, Math.min(vh - 1, sy));
        sw = Math.max(1, Math.min(vw - sx, sw));
        sh = Math.max(1, Math.min(vh - sy, sh));

        const size = Math.min(sw, sh);
        sx = sx + (sw - size) / 2;
        sy = sy + (sh - size) / 2;
        sw = size;
        sh = size;

        const canvas = document.createElement("canvas");
        canvas.width = 640;
        canvas.height = 640;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

        return await new Promise((resolve) => {
          canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.8);
        });
      } catch (error) {
        console.error("Ошибка при извлечении изображения лица:", error);
        return null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.qr-code {
  @apply w-full p-5 flex items-center justify-center;
}

.face-border {
  @apply relative w-full flex items-center justify-center p-5;
  position: relative;

  &::before {
    content: "";
    display: block;
    padding-bottom: 100%;
  }

  .loading-overlay {
    @apply fixed top-0 left-0 w-full h-full z-[2000] flex items-center justify-center;
    background-color: hsl(var(--background));
  }

  .instruction-text {
    @apply absolute top-8 left-0 w-full text-center text-sm text-white px-4;
    z-index: 1100;
    pointer-events: none;

    & > div {
      @apply inline-block py-2 px-4 leading-snug;
      background-color: rgba(0, 0, 0, 0.55);
      border-radius: 9999px;
      max-width: min(90%, 480px);
    }
  }

  #video-container {
    @apply absolute top-0 left-0 w-full h-full flex justify-center items-center;
    video {
      object-fit: cover;
      object-position: center;
      width: 100%;
      height: 100%;
    }
  }

  .scanning-line {
    position: absolute;
    left: 0;
    right: 0;
    height: 25%;
    top: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 255, 136, 0.2) 20%,
      rgba(0, 255, 136, 0.15) 30%,
      rgba(0, 255, 136, 0.1) 40%,
      rgba(0, 255, 136, 0.05) 50%,
      rgba(0, 255, 136, 0) 60%
    );
    animation: scanning 1.5s linear infinite alternate;
    z-index: 100;
    border-top-width: 1.5px;
    border-color: rgba(0, 255, 136, 1);
  }
}

@keyframes scanning {
  0% {
    top: 0;
  }
  100% {
    top: 100%;
  }
}
</style>
